import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  CalendarCheck,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  FileSignature,
  Palmtree,
  Target,
  UserRound,
  Users,
} from "lucide-react";

import { ApiError, api } from "../api/client";
import BottomNav from "../components/BottomNav";
import PeerStatusChips, { type PeerStatus } from "../components/PeerStatusChips";
import { appraisalName } from "../utils/appraisal";
import ReviewStagePips from "../components/ReviewStagePips";
import MeetingPanel, { type MeetingInfo } from "../components/appraisal/MeetingPanel";
import CheckInsCard from "../components/appraisal/CheckInsCard";
import { useAuth } from "../context/AuthContext";
import type { LeaveRequest } from "../types";
import {
  formatLeaveTypeName,
} from "../utils/formatters";
import { reviewStatusLabel, reviewStatusNext, reviewStatusTone } from "../utils/reviewStatus";

const DATE_FMT = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });

interface TeamMember {
  id: number;
  name: string;
  employee_number: string;
  job_title: string | null;
  site_name: string | null;
  department_name: string | null;
  photo_path: string | null;
  annual_leave_remaining: string | null;
  on_leave_today: boolean;
  next_leave: OffEntry | null;
  job_description_status?: "none" | "draft" | "issued" | "changes_requested" | "signed";
}

interface OffEntry {
  employee_id: number;
  employee_name: string;
  leave_type_name: string | null;
  leave_type_colour: string | null;
  start_date: string;
  end_date: string;
  total_days: string;
  is_today: boolean;
}

interface TeamReview {
  id: number;
  employee_id: number;
  employee_name: string;
  cycle_name: string;
  appraisal_year?: number | null;
  status: string;
  next_action: string;
  deadline: string | null;
  overdue: boolean;
  assigned_to_me: boolean;
  peers: PeerStatus[];
  /** The booked review meeting, once there is one. */
  meeting?: MeetingInfo | null;
}

interface DueForReview {
  employee_id: number;
  employee_name: string;
  last_review_date: string | null;
}

interface OpenTask {
  id: number;
  title: string;
  task_type: string;
  priority: string;
  due_date: string | null;
  overdue: boolean;
  action_url: string | null;
}

interface Overview {
  can_approve_leave: boolean;
  preview_of: string | null;
  counts: {
    team_size: number;
    pending_leave: number;
    off_today: number;
    reviews_waiting_on_you: number;
    reviews_overdue: number;
    reviews_due: number;
    open_tasks: number;
  };
  members: TeamMember[];
  off_this_week: OffEntry[];
  active_reviews: TeamReview[];
  due_for_review: DueForReview[];
  open_tasks: OpenTask[];
}

export default function TeamPage() {
  const { t } = useTranslation();
  const { employee, isAdmin, hasPermission, viewAsLevel } = useAuth();
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);

  // When an admin previews a specific employee, show THAT person's team
  // (read-only) rather than the admin's own. The real account must be an
  // admin for the server to allow it.
  const previewId = viewAsLevel?.employee_id ?? null;
  const realAdmin = !!employee?.is_admin;

  const canSee =
    !!employee &&
    ((previewId != null && realAdmin) ||
      isAdmin || hasPermission("can_view_team") || hasPermission("can_approve_leave"));

  const load = useCallback(async () => {
    try {
      const qs = previewId != null ? `?preview_employee_id=${previewId}` : "";
      setData(await api.get<Overview>(`/api/team/overview${qs}`));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    }
  }, [previewId]);

  useEffect(() => {
    if (canSee) void load();
  }, [canSee, load]);

  if (!canSee) {
    return (
      <div className="flex min-h-dvh flex-col bg-pme-paper safe-x">
        <header className="safe-top px-5 pt-4 pb-3">
          <h1 className="font-display text-3xl text-pme-navy">{t("team.title", "My Team")}</h1>
        </header>
        <main className="flex-1 px-5 py-4 pb-24">
          <p className="text-sm text-gray-500">
            {t("team.managersOnly", "This page is for managers and team leaders.")}
          </p>
        </main>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh flex-col bg-pme-paper safe-x">
      <header className="safe-top px-5 pt-4 pb-3 pr-28">
        <p className="eyebrow">{t("team.eyebrow", "Manager view")}</p>
        <h1 className="font-display text-3xl text-pme-navy">{t("team.title", "My Team")}</h1>
        {data?.preview_of && (
          <p className="mt-1 text-xs font-medium text-amber-700">
            {t("team.previewNote", "Showing {{name}}'s team — read-only preview.", { name: data.preview_of })}
          </p>
        )}
      </header>

      <main className="flex-1 overflow-y-auto px-5 pb-24 space-y-4">
        {error && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
        {!data && !error && <p className="text-sm text-gray-400">{t("common.loading")}</p>}

        {data && (
          <>
            <AttentionStrip counts={data.counts} />
            {data.can_approve_leave && <ApprovalsSection onChanged={load} />}
            <ReviewsSection data={data} onChanged={load} readOnly={!!data.preview_of} />
            {!data.preview_of && <CheckInsCard />}
            <OffThisWeekSection entries={data.off_this_week} />
            {data.open_tasks.length > 0 && <TasksSection tasks={data.open_tasks} readOnly={!!data.preview_of} />}
            <MembersSection members={data.members} readOnly={!!data.preview_of} />
          </>
        )}
      </main>

      <BottomNav />
    </div>
  );
}

/* ---------- Needs your attention ---------- */

function AttentionStrip({ counts }: { counts: Overview["counts"] }) {
  const { t } = useTranslation();
  const plural = (count: number, key: string, one: string, other: string) =>
    t(key, count === 1 ? one : other, { count });
  const chips: { label: string; count: number; tone: string }[] = [
    { label: plural(counts.pending_leave, counts.pending_leave === 1 ? "team.chips.holidayApprovalsOne" : "team.chips.holidayApprovals", "holiday request to approve", "holiday requests to approve"), count: counts.pending_leave, tone: "bg-amber-50 text-amber-800 border-amber-200" },
    { label: plural(counts.reviews_waiting_on_you, counts.reviews_waiting_on_you === 1 ? "team.chips.reviewsOnYouOne" : "team.chips.reviewsOnYou", "review waiting on you", "reviews waiting on you"), count: counts.reviews_waiting_on_you, tone: "bg-blue-50 text-blue-700 border-blue-200" },
    { label: plural(counts.reviews_overdue, counts.reviews_overdue === 1 ? "team.chips.reviewsOverdueOne" : "team.chips.reviewsOverdue", "review overdue", "reviews overdue"), count: counts.reviews_overdue, tone: "bg-red-50 text-red-700 border-red-200" },
    { label: plural(counts.reviews_due, counts.reviews_due === 1 ? "team.chips.reviewsDueOne" : "team.chips.reviewsDue", "person due a review", "people due a review"), count: counts.reviews_due, tone: "bg-indigo-50 text-indigo-700 border-indigo-200" },
    { label: t("team.chips.offToday", "off today"), count: counts.off_today, tone: "bg-gray-50 text-gray-600 border-gray-200" },
    { label: plural(counts.open_tasks, counts.open_tasks === 1 ? "team.chips.openTasksOne" : "team.chips.openTasks", "open task", "open tasks"), count: counts.open_tasks, tone: "bg-purple-50 text-purple-700 border-purple-200" },
  ];
  const visible = chips.filter((c) => c.count > 0);
  return (
    <section className="card p-4">
      <SectionHeading icon={<ClipboardList size={16} strokeWidth={1.75} aria-hidden />}>
        {t("team.needsAttention", "Needs your attention")}
      </SectionHeading>
      {visible.length === 0 ? (
        <p className="mt-2 flex items-center gap-1.5 text-sm text-gray-500">
          <CheckCircle2 size={15} strokeWidth={1.75} className="text-green-600" aria-hidden />
          {t("team.allClear", "All clear — nothing is waiting on you.")}
        </p>
      ) : (
        <div className="mt-2 flex flex-wrap gap-2">
          {visible.map((c) => (
            <span key={c.label} className={`rounded-full border px-3 py-1 text-xs font-medium ${c.tone}`}>
              <span className="font-semibold">{c.count}</span> {c.label}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

/* ---------- Holiday approvals ---------- */

function ApprovalsSection({ onChanged }: { onChanged: () => void }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<LeaveRequest[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState<{ id: number; mode: "approve" | "reject" | "amend" } | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await api.get<LeaveRequest[]>("/api/leave/team-requests?status=pending"));
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const refresh = () => {
    setActing(null);
    void load();
    onChanged();
  };

  return (
    <section className="card p-4">
      <SectionHeading icon={<Palmtree size={16} strokeWidth={1.75} aria-hidden />}>
        {t("team.holidayApprovals", "Holiday approvals")}
      </SectionHeading>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      {!error && rows.length === 0 && (
        <p className="mt-2 text-sm text-gray-500">{t("team.noPendingLeave", "No holiday requests waiting for you.")}</p>
      )}
      <ul className="mt-2 space-y-2">
        {rows.map((r) => (
          <li key={r.id} className="rounded-lg border border-gray-100 bg-gray-50/60 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">{r.employee_name}</p>
                <p className="text-xs text-gray-600">
                  {formatLeaveTypeName(r.leave_type_code, r.leave_type_name)} · {DATE_FMT.format(new Date(r.start_date))} → {DATE_FMT.format(new Date(r.end_date))} · {r.total_days} {t("leave.days").toLowerCase()}
                </p>
                {r.reason && <p className="mt-1 text-xs text-gray-500">{r.reason}</p>}
              </div>
            </div>
            {acting?.id === r.id ? (
              <ApprovalActionForm request={r} mode={acting.mode} onDone={refresh} onCancel={() => setActing(null)} />
            ) : (
              <div className="mt-2 flex flex-wrap gap-2">
                <button type="button" onClick={() => setActing({ id: r.id, mode: "approve" })} className="min-h-[40px] rounded-lg bg-green-600 px-3 text-xs font-semibold text-white hover:bg-green-700">
                  {t("leave.approve")}
                </button>
                <button type="button" onClick={() => setActing({ id: r.id, mode: "amend" })} className="min-h-[40px] rounded-lg border border-orange-300 px-3 text-xs font-semibold text-orange-700 hover:bg-orange-50">
                  {t("leave.amend")}
                </button>
                <button type="button" onClick={() => setActing({ id: r.id, mode: "reject" })} className="min-h-[40px] rounded-lg border border-red-300 px-3 text-xs font-semibold text-red-600 hover:bg-red-50">
                  {t("leave.reject")}
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function ApprovalActionForm({
  request,
  mode,
  onDone,
  onCancel,
}: {
  request: LeaveRequest;
  mode: "approve" | "reject" | "amend";
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState("");
  const [startDate, setStartDate] = useState(request.start_date);
  const [endDate, setEndDate] = useState(request.end_date);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if ((mode === "reject" || mode === "amend") && !notes.trim()) {
      setError(t("team.reasonRequired", "Please give a reason."));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (mode === "approve") {
        await api.put(`/api/leave/requests/${request.id}/approve`, { notes: notes.trim() || null });
      } else if (mode === "reject") {
        await api.put(`/api/leave/requests/${request.id}/reject`, { notes: notes.trim() });
      } else {
        await api.put(`/api/leave/requests/${request.id}/amend`, {
          start_date: startDate,
          end_date: endDate,
          is_half_day_start: false,
          is_half_day_end: false,
          notes: notes.trim(),
        });
      }
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-3 space-y-2 rounded-lg border border-gray-200 bg-white p-3">
      {mode === "amend" && (
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-xs">
            <span className="font-medium text-gray-700">{t("leave.startDate")}</span>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="input mt-1" />
          </label>
          <label className="block text-xs">
            <span className="font-medium text-gray-700">{t("leave.endDate")}</span>
            <input type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} className="input mt-1" />
          </label>
        </div>
      )}
      <label className="block text-xs">
        <span className="font-medium text-gray-700">
          {mode === "approve"
            ? t("team.notesOptional", "Notes (optional)")
            : mode === "reject"
              ? t("leave.rejectReason")
              : t("leave.amendmentNotes")}
        </span>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="input mt-1" />
      </label>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void submit()}
          disabled={submitting}
          className={[
            "min-h-[40px] rounded-lg px-4 text-xs font-semibold text-white disabled:opacity-60",
            mode === "approve" ? "bg-green-600" : mode === "reject" ? "bg-red-600" : "bg-orange-500",
          ].join(" ")}
        >
          {submitting
            ? t("common.loading")
            : mode === "approve"
              ? t("leave.approve")
              : mode === "reject"
                ? t("leave.reject")
                : t("leave.submitAmendment")}
        </button>
        <button type="button" onClick={onCancel} className="min-h-[40px] rounded-lg border border-gray-300 px-4 text-xs font-semibold text-gray-700">
          {t("common.cancel")}
        </button>
      </div>
    </div>
  );
}

/* ---------- Reviews ---------- */

function ReviewsSection({ data, onChanged, readOnly }: { data: Overview; onChanged: () => void; readOnly?: boolean }) {
  const { t } = useTranslation();
  return (
    <section className="card p-4">
      <SectionHeading icon={<Target size={16} strokeWidth={1.75} aria-hidden />}>
        {t("team.reviews", "Reviews")}
      </SectionHeading>

      {data.active_reviews.length === 0 && data.due_for_review.length === 0 && (
        <p className="mt-2 text-sm text-gray-500">{t("team.noReviews", "No reviews in progress and nobody is due one.")}</p>
      )}

      {data.due_for_review.length > 0 && (
        <div className="mt-2 rounded-lg border border-indigo-100 bg-indigo-50/60 p-3">
          <p className="text-xs font-semibold text-indigo-800">
            {t("team.dueForReview", "Due a review")}
          </p>
          <ul className="mt-1.5 space-y-1">
            {data.due_for_review.map((d) => (
              <li key={d.employee_id} className="flex items-center justify-between gap-2 text-sm text-gray-800">
                <span className="font-medium">{d.employee_name}</span>
                <span className="text-xs text-gray-500">
                  {d.last_review_date
                    ? t("team.lastReviewed", "Last reviewed {{date}}", { date: DATE_FMT.format(new Date(d.last_review_date)) })
                    : t("team.neverReviewed", "No review on record")}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-gray-500">
            {t("team.dueForReviewHint", "Ask HR to schedule these, or start them from Admin → Performance if you have access.")}
          </p>
        </div>
      )}

      <ul className="mt-2 space-y-2">
        {data.active_reviews.map((rv) => (
          <li key={rv.id} className="rounded-lg border border-gray-100 bg-gray-50/60 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">{rv.employee_name}</p>
                <p className="text-xs text-gray-500">{appraisalName(rv.appraisal_year)}</p>
              </div>
              <div className="flex items-center gap-2">
                {rv.deadline && (
                  <span className={`text-[11px] ${rv.overdue ? "font-semibold text-red-600" : "text-gray-400"}`}>
                    {rv.overdue ? t("team.overdueSince", "Overdue since {{date}}", { date: DATE_FMT.format(new Date(rv.deadline)) }) : t("team.due", "Due {{date}}", { date: DATE_FMT.format(new Date(rv.deadline)) })}
                  </span>
                )}
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${reviewStatusTone(rv.status)}`}>
                  {reviewStatusLabel(rv.status)}
                </span>
              </div>
            </div>
            <div className="mt-2">
              <ReviewStagePips status={rv.status} />
            </div>
            <p className="mt-1.5 text-xs text-gray-500">{reviewStatusNext(rv.status)}</p>
            {rv.assigned_to_me && (rv.peers.length > 0 || rv.status === "self_assessment_submitted" || rv.status === "colleague_feedback_in_progress") && (
              <div className="mt-2 rounded-lg border border-gray-100 bg-white px-2.5 py-2">
                <p className="text-[11px] font-semibold text-gray-600">
                  {t("team.colleaguesReviewing", "Colleagues giving feedback")}
                </p>
                <PeerStatusChips peers={rv.peers} canResend={!readOnly} />
              </div>
            )}
            {rv.assigned_to_me && !readOnly && rv.next_action === "hold_meeting" && (
              <div className="mt-2">
                <MeetingPanel reviewId={rv.id} employeeName={rv.employee_name} status={rv.status} meeting={rv.meeting} compact onChanged={onChanged} />
              </div>
            )}
            {rv.assigned_to_me && !readOnly && (
              <div className="mt-2 flex flex-wrap gap-2">
                {(rv.next_action === "your_review") && (
                  <Link
                    to={`/review?review=${rv.id}&tab=manager-review&step=1`}
                    className="min-h-[40px] inline-flex items-center rounded-lg bg-pme-blue px-3 text-xs font-semibold text-white hover:bg-pme-blue/90"
                  >
                    {rv.status === "manager_review_in_progress"
                      ? t("team.continueManagerReview", "Continue manager review")
                      : t("team.startManagerReview", "Start manager review")}
                  </Link>
                )}
                <Link
                  to={`/review?review=${rv.id}`}
                  className="min-h-[40px] inline-flex items-center rounded-lg border border-gray-200 px-3 text-xs font-medium text-gray-600 hover:bg-gray-50"
                >
                  {t("team.viewReview", "View review")}
                </Link>
                {(rv.status === "self_assessment_submitted" || rv.status === "colleague_feedback_in_progress") && (
                  <Link
                    to="/performance?tab=team-reviews"
                    className="min-h-[40px] inline-flex items-center rounded-lg border border-gray-200 px-3 text-xs font-medium text-gray-600 hover:bg-gray-50"
                  >
                    {t("team.chooseColleagues", "Choose colleagues")}
                  </Link>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

    </section>
  );
}

/* ---------- Off this week ---------- */

function OffThisWeekSection({ entries }: { entries: OffEntry[] }) {
  const { t } = useTranslation();
  return (
    <section className="card p-4">
      <SectionHeading icon={<CalendarDays size={16} strokeWidth={1.75} aria-hidden />}>
        {t("team.offThisWeek", "Off today and this week")}
      </SectionHeading>
      {entries.length === 0 ? (
        <p className="mt-2 text-sm text-gray-500">{t("team.nobodyOff", "Nobody in your team is off in the next 7 days.")}</p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {entries.map((e, i) => (
            <li key={`${e.employee_id}-${e.start_date}-${i}`} className="flex items-center justify-between gap-2 text-sm">
              <span className="flex items-center gap-2 min-w-0">
                <span
                  aria-hidden
                  className="inline-block h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: e.leave_type_colour ?? "#0872c1" }}
                />
                <span className="truncate font-medium text-gray-900">{e.employee_name}</span>
                {e.is_today && (
                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                    {t("team.today", "Today")}
                  </span>
                )}
              </span>
              <span className="shrink-0 text-xs text-gray-500">
                {DATE_FMT.format(new Date(e.start_date))} → {DATE_FMT.format(new Date(e.end_date))}
              </span>
            </li>
          ))}
        </ul>
      )}
      <Link to="/calendar" className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-pme-blue hover:underline">
        <CalendarCheck size={13} strokeWidth={2} aria-hidden />
        {t("team.openCalendar", "Open the company calendar")}
      </Link>
    </section>
  );
}

/* ---------- Tasks ---------- */

function TasksSection({ tasks, readOnly }: { tasks: OpenTask[]; readOnly?: boolean }) {
  const { t } = useTranslation();
  return (
    <section className="card p-4">
      <SectionHeading icon={<ClipboardList size={16} strokeWidth={1.75} aria-hidden />}>
        {t("team.yourTasks", "Your open tasks")}
      </SectionHeading>
      <ul className="mt-2 space-y-1.5">
        {tasks.slice(0, 6).map((task) => (
          <li key={task.id} className="flex items-center justify-between gap-2 text-sm">
            <span className="truncate font-medium text-gray-900">{task.title}</span>
            {task.due_date && (
              <span className={`shrink-0 text-xs ${task.overdue ? "font-semibold text-red-600" : "text-gray-500"}`}>
                {t("team.due", "Due {{date}}", { date: DATE_FMT.format(new Date(task.due_date)) })}
              </span>
            )}
          </li>
        ))}
      </ul>
      {!readOnly && (
        <Link to="/performance?tab=tasks" className="mt-3 inline-block text-xs font-semibold text-pme-blue hover:underline">
          {t("team.allTasks", "See all tasks")}
        </Link>
      )}
    </section>
  );
}

/* ---------- Members ---------- */

const JD_CHIP: Record<string, [string, string, string]> = {
  none: ["team.jd.none", "Not written yet", "text-gray-500"],
  draft: ["team.jd.draft", "Draft", "text-gray-600"],
  issued: ["team.jd.issued", "Waiting for signature", "text-pme-blue"],
  changes_requested: ["team.jd.changes", "Change asked for", "text-amber-700"],
  signed: ["team.jd.signed", "Signed", "text-green-700"],
};

function MembersSection({ members, readOnly }: { members: TeamMember[]; readOnly?: boolean }) {
  const { t } = useTranslation();
  return (
    <section className="card p-4">
      <SectionHeading icon={<Users size={16} strokeWidth={1.75} aria-hidden />}>
        {t("team.members", "Team members")}
      </SectionHeading>
      {members.length === 0 ? (
        <p className="mt-2 text-sm text-gray-500">{t("team.noReports", "You have no direct reports in the system yet.")}</p>
      ) : (
        <ul className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {members.map((m) => (
            <li key={m.id} className="flex flex-wrap items-center gap-x-3 gap-y-0 rounded-lg border border-gray-100 bg-gray-50/60 p-3">
              {m.photo_path ? (
                <img src={m.photo_path} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />
              ) : (
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-pme-sky text-pme-navy">
                  <UserRound size={18} strokeWidth={1.75} aria-hidden />
                </span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-gray-900">{m.name}</p>
                {m.job_title && <p className="truncate text-xs font-bold text-gray-700">{m.job_title}</p>}
                <p className="truncate text-[11px] text-gray-500">
                  {[m.department_name, m.site_name].filter(Boolean).join(" · ")}
                </p>
              </div>
              <div className="shrink-0 text-right">
                {m.on_leave_today && (
                  <span className="mb-0.5 block rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                    {t("team.offTodayBadge", "Off today")}
                  </span>
                )}
                {m.annual_leave_remaining != null && (
                  <span className="block text-[11px] text-gray-500">
                    {t("team.leaveLeft", "{{days}} days' leave left", { days: m.annual_leave_remaining })}
                  </span>
                )}
              </div>
                {!readOnly && m.job_description_status && (
                  <Link
                    to={`/job-description/${m.id}`}
                    className="-mt-1 inline-flex min-h-[32px] basis-full items-center gap-1 pl-[52px] text-[11px] font-semibold text-pme-blue"
                  >
                    <FileSignature size={13} strokeWidth={1.75} aria-hidden />
                    {t("team.jd.link", "Job description")}
                    <span className={`font-normal ${JD_CHIP[m.job_description_status]?.[2] ?? "text-gray-500"}`}>
                      · {t(JD_CHIP[m.job_description_status]?.[0] ?? "", JD_CHIP[m.job_description_status]?.[1] ?? "")}
                    </span>
                  </Link>
                )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function SectionHeading({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <h2 className="flex items-center gap-2 text-sm font-semibold text-pme-navy">
      <span className="text-pme-blue">{icon}</span>
      {children}
    </h2>
  );
}
